import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Widget,
  WidgetHeader,
  WidgetContent,
} from "@/components/consultation/shared/Widget";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  MessageSquare,
  History,
  Check,
  MoreHorizontal,
  Pencil,
  Trash2,
} from "lucide-react";
import { EmptyState } from "@/components/consultation/EmptyState";
import { useGeneralAdvice } from "@/hooks/useGeneralAdvice";
import { GeneralAdviceHistoryDialog } from "./GeneralAdviceHistoryDialog";
import type { ParentType } from "@/lib/general-advice-api";

interface ItemState {
  text: string;
  draftText: string;
  isEditing: boolean;
  isNew: boolean;
}

interface GeneralAdviceCardProps {
  appointmentId: string;
  isCompleted?: boolean;
  parentType?: ParentType;
}

export const GeneralAdviceCard = ({
  appointmentId,
  isCompleted = false,
  parentType = "prescriptions",
}: GeneralAdviceCardProps) => {
  const {
    data: generalAdviceData,
    isLoading,
    create,
    update,
  } = useGeneralAdvice(appointmentId, false, parentType);

  const generalAdvice = Array.isArray(generalAdviceData)
    ? generalAdviceData[0]
    : generalAdviceData;

  const [items, setItems] = useState<ItemState[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);

  useEffect(() => {
    if (generalAdvice?.advices && !isInitialized) {
      setItems(
        generalAdvice.advices.map((text) => ({
          text,
          draftText: text,
          isEditing: false,
          isNew: false,
        }))
      );
      setIsInitialized(true);
    } else if (!generalAdvice && !isLoading && !isInitialized) {
      setItems([]);
      setIsInitialized(true);
    }
  }, [generalAdvice, isInitialized, isLoading]);

  const saveToServer = async (texts: string[]) => {
    const clean = texts.filter((t) => t.trim() !== "");
    try {
      if (generalAdvice?.id) {
        await update.mutateAsync({
          id: generalAdvice.id,
          data: { advices: clean },
        });
      } else if (clean.length > 0) {
        await create.mutateAsync({ advices: clean });
      }
    } catch (error) {
      console.error("Failed to save general advice:", error);
      toast.error(
        "Couldn't save general advice. Check the list and try again."
      );
    }
  };

  const handleAddRow = () => {
    setItems((prev) => [
      ...prev,
      { text: "", draftText: "", isEditing: true, isNew: true },
    ]);
  };

  const handleConfirm = (index: number) => {
    const item = items[index];
    const trimmed = item.draftText.trim();
    if (!trimmed) {
      setItems((prev) =>
        item.isNew
          ? prev.filter((_, i) => i !== index)
          : prev.map((it, i) =>
              i === index ? { ...it, draftText: it.text, isEditing: false } : it
            )
      );
      return;
    }
    const newItems = items.map((it, i) =>
      i === index
        ? {
            ...it,
            text: trimmed,
            draftText: trimmed,
            isEditing: false,
            isNew: false,
          }
        : it
    );
    setItems(newItems);
    saveToServer(newItems.filter((it) => !it.isNew).map((it) => it.text));
  };

  const handleCancelEdit = (index: number) => {
    const item = items[index];
    if (item.isNew) {
      setItems((prev) => prev.filter((_, i) => i !== index));
    } else {
      setItems((prev) =>
        prev.map((it, i) =>
          i === index ? { ...it, draftText: it.text, isEditing: false } : it
        )
      );
    }
  };

  const handleEdit = (index: number) => {
    setItems((prev) =>
      prev.map((it, i) => (i === index ? { ...it, isEditing: true } : it))
    );
  };

  const handleDraftChange = (index: number, value: string) => {
    setItems((prev) =>
      prev.map((it, i) => (i === index ? { ...it, draftText: value } : it))
    );
  };

  const handleDeleteConfirm = () => {
    if (deleteIndex === null) return;
    const newItems = items.filter((_, i) => i !== deleteIndex);
    setItems(newItems);
    setDeleteIndex(null);
    saveToServer(newItems.filter((it) => !it.isNew).map((it) => it.text));
  };

  const isSaving = create.isPending || update.isPending;
  const hasItems = items.length > 0;

  return (
    <>
      <Widget>
        <WidgetHeader
          title="General Advice"
          hasInfo
          onAdd={isCompleted ? undefined : handleAddRow}
          extras={
            <>
              {isSaving && (
                <Loader2 className="size-3 animate-spin text-muted-foreground" />
              )}
              <button
                type="button"
                className="flex items-center justify-center size-5 rounded-md bg-muted text-muted-foreground hover:bg-muted/70 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsHistoryDialogOpen(true);
                }}
                title="View History"
              >
                <History className="size-3" />
              </button>
            </>
          }
        />
        <WidgetContent>
          {isLoading && !isInitialized ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="size-5 animate-spin mr-2" />
              <span className="z-secondary">Loading advice…</span>
            </div>
          ) : !hasItems ? (
            <EmptyState
              icon={<MessageSquare />}
              title="No general advice added"
              variant="compact"
            />
          ) : (
            <div className="space-y-1">
              {items.map((item, index) =>
                item.isEditing ? (
                  <div key={index} className="flex items-start gap-2">
                    <Textarea
                      rows={1}
                      autoFocus
                      value={item.draftText}
                      onChange={(e) => handleDraftChange(index, e.target.value)}
                      placeholder="Enter advice (e.g. Drink plenty of water)"
                      disabled={isCompleted}
                      className="min-h-9 py-2 z-secondary"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleConfirm(index);
                        }
                        if (e.key === "Escape") {
                          handleCancelEdit(index);
                        }
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8 shrink-0 text-muted-foreground hover:text-primary hover:bg-primary/10"
                      onClick={() => handleConfirm(index)}
                      disabled={isCompleted}
                      title="Confirm"
                    >
                      <Check className="size-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div
                    key={index}
                    className="group flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted/40 transition-colors"
                  >
                    <p className="z-secondary text-foreground flex-1 leading-snug">
                      {item.text}
                    </p>
                    {!isCompleted && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-7 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <MoreHorizontal className="size-3.5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(index)}>
                            <Pencil className="size-3.5 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => setDeleteIndex(index)}
                          >
                            <Trash2 className="size-3.5 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                )
              )}
            </div>
          )}
        </WidgetContent>
      </Widget>

      <AlertDialog
        open={deleteIndex !== null}
        onOpenChange={(open) => !open && setDeleteIndex(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete advice?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this advice entry.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteConfirm}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <GeneralAdviceHistoryDialog
        open={isHistoryDialogOpen}
        onOpenChange={setIsHistoryDialogOpen}
        appointmentId={appointmentId}
        parentType={parentType}
      />
    </>
  );
};
